import { useEffect, useRef } from 'react';
import { useLocalSearchParams, useNavigation } from 'expo-router';
import { useSelector } from 'react-redux';
import { selectActiveInventory } from '../../redux/inventorySlice';

/**
 * `/inventory?import=<importId>` opens that delivery's review — the link the
 * Lufa import email ends with.
 *
 * The rows usually are not there yet when the page boots: the email's link
 * opens a fresh tab, and the pantry arrives with the first sync. So this waits
 * for them, opens the review once, and takes the parameter back off the URL so
 * a reload does not open it again.
 *
 * Only a basket still waiting for review opens. The URL can come back without
 * the email — NavigationTracker stores it and a relaunch restores it — and a
 * basket the cook already went through has nothing left to ask. An import that
 * never shows up (another account, every row eaten) leaves the pantry as it is.
 */
export function useImportLink(onOpen: (importId: string) => void): void {
  const params = useLocalSearchParams<{ import?: string | string[] }>();
  // The screen's own navigation, not the global router: `router.setParams`
  // goes through the root ref and lands on the (app) group, leaving the
  // parameter on the URL.
  const navigation = useNavigation();
  const items = useSelector(selectActiveInventory);
  const handled = useRef<string | null>(null);

  const importId = typeof params.import === 'string' ? params.import : undefined;
  const waiting = !!importId && items.some(item => item?.importId === importId && item.reviewed === false);

  useEffect(() => {
    if (!importId || !waiting || handled.current === importId) return;
    handled.current = importId;
    // Deferred past this commit: on a cold load this effect runs before the
    // tab navigator's own effects (children first), and a setParams dispatched
    // then is dropped — the URL kept ?import= and every reload reopened it.
    queueMicrotask(() => navigation.setParams({ import: undefined } as never));
    onOpen(importId);
  }, [importId, waiting, navigation, onOpen]);
}
